import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { brand } from "@/content/site";
import { track } from "@/lib/analytics";
import { AlertTriangle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const goals = [
  "Improve local visibility",
  "Generate more enquiries",
  "Improve lead follow-up",
  "Book more jobs",
  "Reactivate customers or old leads",
  "Improve systems and automation",
  "Not sure yet",
];
const planOptions = [
  "Get Found",
  "Get Leads",
  "Cruise Control",
  "Energy Growth Pilot",
  "Installer Growth Pilot",
  "Help me choose",
];
const teamSizes = ["Just me", "2–5", "6–10", "11–20", "20+"];
const contactMethods = ["Email", "Phone", "Either"];
const installerProductOptions = [
  "Solar PV",
  "Batteries",
  "Solar + Battery",
  "Other",
];
const yesNoUnsure = ["Yes", "No", "Not sure"];

function FieldError({ message }) {
  if (!message) return null;
  return (
    <p role="alert" className="mt-1.5 text-sm font-medium text-red-600">
      {message}
    </p>
  );
}

export default function EnquiryForm() {
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState("");

  // Deep-link prefill: /contact?interest=installer-growth-pilot
  const initialInterest = (() => {
    if (typeof window === "undefined") return "";
    const p = new URLSearchParams(window.location.search).get("interest");
    if (p === "installer-growth-pilot") return "Installer Growth Pilot";
    if (p === "energy-growth-pilot") return "Energy Growth Pilot";
    return "";
  })();

  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      businessName: "",
      email: "",
      phone: "",
      tradeType: "",
      serviceArea: "",
      website: "",
      teamSize: "",
      primaryGoal: "",
      interestedPlan: initialInterest,
      message: "",
      preferredContact: "",
      consent: false,
      company_website: "", // honeypot
      // installer-pilot qualification fields (conditional)
      installerSubcontracts: "",
      installerProducts: "",
      installerTerritory: "",
      installerCapacityWeek: "",
      installerDirectCustomers: "",
      installerNetApprovedSeller: "",
      installerSolarVictoria: "",
      installerSaaAccreditation: "",
      installerApprovedContracts: "",
      installerLandingPrices: "",
      installerSubcontractPct: "",
      installerDesiredOutcome: "",
    },
  });

  const interestedPlan = watch("interestedPlan");
  const showInstallerFields = interestedPlan === "Installer Growth Pilot";

  const [meta, setMeta] = useState({});

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setMeta({
      landingPage: window.location.href,
      referrer: document.referrer || "",
      utmSource: params.get("utm_source") || "",
      utmMedium: params.get("utm_medium") || "",
      utmCampaign: params.get("utm_campaign") || "",
      utmTerm: params.get("utm_term") || "",
      utmContent: params.get("utm_content") || "",
      refToken: params.get("ref") || sessionStorage.getItem("gc_ref") || "",
    });
  }, []);

  const onSubmit = async (data) => {
    setSubmitError("");
    try {
      const res = await axios.post(`${API}/enquiries`, { ...data, ...meta });
      if (res.status === 200 && res.data?.id) {
        track("growth_assessment_submitted", {
          plan: data.interestedPlan,
          goal: data.primaryGoal,
        });
        navigate("/thank-you");
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      track("contact_form_error", {
        status: err?.response?.status || "network",
      });
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong sending your request.";
      setSubmitError(detail);
      toast.error("We couldn't submit your request", {
        description: `${detail} You can also email ${brand.email}.`,
      });
    }
  };

  const onInvalid = () => {
    toast.error("Please complete the required fields highlighted below.");
  };

  const inputCls =
    "h-14 bg-white border-border-neutral rounded-xl focus-visible:ring-mint text-base";

  return (
    <form
      onSubmit={handleSubmit(onSubmit, onInvalid)}
      noValidate
      data-testid="enquiry-form"
      className="space-y-5"
    >
      {/* honeypot */}
      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
        {...register("company_website")}
      />

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="firstName">First name *</Label>
          <Input
            id="firstName"
            data-testid="input-firstName"
            className={inputCls}
            aria-invalid={!!errors.firstName}
            {...register("firstName", { required: "First name is required" })}
          />
          <FieldError message={errors.firstName?.message} />
        </div>
        <div>
          <Label htmlFor="lastName">Last name *</Label>
          <Input
            id="lastName"
            data-testid="input-lastName"
            className={inputCls}
            aria-invalid={!!errors.lastName}
            {...register("lastName", { required: "Last name is required" })}
          />
          <FieldError message={errors.lastName?.message} />
        </div>
      </div>

      <div>
        <Label htmlFor="businessName">Business name *</Label>
        <Input
          id="businessName"
          data-testid="input-businessName"
          className={inputCls}
          aria-invalid={!!errors.businessName}
          {...register("businessName", { required: "Business name is required" })}
        />
        <FieldError message={errors.businessName?.message} />
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="email">Work email *</Label>
          <Input
            id="email"
            type="email"
            data-testid="input-email"
            className={inputCls}
            aria-invalid={!!errors.email}
            {...register("email", {
              required: "Work email is required",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Enter a valid email address",
              },
            })}
          />
          <FieldError message={errors.email?.message} />
        </div>
        <div>
          <Label htmlFor="phone">Mobile or business phone *</Label>
          <Input
            id="phone"
            type="tel"
            data-testid="input-phone"
            className={inputCls}
            aria-invalid={!!errors.phone}
            {...register("phone", {
              required: "Phone number is required",
              minLength: { value: 6, message: "Enter a valid phone number" },
            })}
          />
          <FieldError message={errors.phone?.message} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="tradeType">Primary trade / business type *</Label>
          <Input
            id="tradeType"
            data-testid="input-tradeType"
            placeholder="e.g. Electrician"
            className={inputCls}
            aria-invalid={!!errors.tradeType}
            {...register("tradeType", { required: "Trade type is required" })}
          />
          <FieldError message={errors.tradeType?.message} />
        </div>
        <div>
          <Label htmlFor="serviceArea">Primary suburb or service area *</Label>
          <Input
            id="serviceArea"
            data-testid="input-serviceArea"
            placeholder="e.g. Geelong VIC"
            className={inputCls}
            aria-invalid={!!errors.serviceArea}
            {...register("serviceArea", { required: "Service area is required" })}
          />
          <FieldError message={errors.serviceArea?.message} />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="website">Current website (optional)</Label>
          <Input
            id="website"
            data-testid="input-website"
            placeholder="https://"
            className={inputCls}
            {...register("website")}
          />
        </div>
        <div>
          <Label htmlFor="teamSize">Approximate team size</Label>
          <Controller
            control={control}
            name="teamSize"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger
                  id="teamSize"
                  data-testid="select-teamSize"
                  className={inputCls}
                >
                  <SelectValue placeholder="Select" />
                </SelectTrigger>
                <SelectContent>
                  {teamSizes.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="primaryGoal">Which result matters most? *</Label>
          <Controller
            control={control}
            name="primaryGoal"
            rules={{ required: "Please choose a goal" }}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger
                  id="primaryGoal"
                  data-testid="select-primaryGoal"
                  className={inputCls}
                  aria-invalid={!!errors.primaryGoal}
                >
                  <SelectValue placeholder="Select a result" />
                </SelectTrigger>
                <SelectContent>
                  {goals.map((g) => (
                    <SelectItem key={g} value={g}>
                      {g}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          <FieldError message={errors.primaryGoal?.message} />
        </div>
        <div>
          <Label htmlFor="interestedPlan">Which plan are you interested in? *</Label>
          <Controller
            control={control}
            name="interestedPlan"
            rules={{ required: "Please choose a plan" }}
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger
                  id="interestedPlan"
                  data-testid="select-interestedPlan"
                  className={inputCls}
                  aria-invalid={!!errors.interestedPlan}
                >
                  <SelectValue placeholder="Select a plan" />
                </SelectTrigger>
                <SelectContent>
                  {planOptions.map((p) => (
                    <SelectItem key={p} value={p}>
                      {p}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
          <FieldError message={errors.interestedPlan?.message} />
        </div>
      </div>

      {/* Conditional: Installer Growth Pilot qualification */}
      {showInstallerFields && (
        <div
          data-testid="installer-pilot-qualification"
          className="rounded-2xl bg-soft/60 border border-mint/40 p-5 md:p-6 space-y-4"
        >
          <div className="flex items-start gap-3">
            <div className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-forest text-lime shrink-0">
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6L9 17l-5-5"/></svg>
            </div>
            <div>
              <p className="text-sm font-extrabold text-forest">
                Installer Growth Pilot — quick eligibility
              </p>
              <p className="text-xs text-mid-neutral leading-relaxed">
                A few questions so we can assess suitability. Used only to
                respond to your enquiry.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="installerSubcontracts">
                Do you currently complete subcontracted solar or battery installations?
              </Label>
              <Controller
                control={control}
                name="installerSubcontracts"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="installerSubcontracts" data-testid="installer-subcontracts" className={inputCls}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {yesNoUnsure.map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div>
              <Label htmlFor="installerProducts">Which products do you install?</Label>
              <Controller
                control={control}
                name="installerProducts"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="installerProducts" data-testid="installer-products" className={inputCls}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {installerProductOptions.map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="installerTerritory">Primary installation territory</Label>
              <Input
                id="installerTerritory"
                data-testid="installer-territory"
                placeholder="e.g. Greater Melbourne"
                className={inputCls}
                {...register("installerTerritory")}
              />
            </div>
            <div>
              <Label htmlFor="installerCapacityWeek">
                Approximate installation capacity per week
              </Label>
              <Input
                id="installerCapacityWeek"
                data-testid="installer-capacity"
                placeholder="e.g. 5–10 jobs"
                className={inputCls}
                {...register("installerCapacityWeek")}
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="installerDirectCustomers">
                Do you currently contract directly with residential customers?
              </Label>
              <Controller
                control={control}
                name="installerDirectCustomers"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="installerDirectCustomers" data-testid="installer-direct-customers" className={inputCls}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {yesNoUnsure.map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div>
              <Label htmlFor="installerNetApprovedSeller">
                NET Approved Seller?
              </Label>
              <Controller
                control={control}
                name="installerNetApprovedSeller"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="installerNetApprovedSeller" data-testid="installer-net-seller" className={inputCls}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {yesNoUnsure.map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="installerSolarVictoria">
                Authorised Solar Victoria retailer (if relevant)?
              </Label>
              <Controller
                control={control}
                name="installerSolarVictoria"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="installerSolarVictoria" data-testid="installer-solar-vic" className={inputCls}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {[...yesNoUnsure, "Not applicable"].map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div>
              <Label htmlFor="installerSaaAccreditation">
                Installers hold required SAA accreditation?
              </Label>
              <Controller
                control={control}
                name="installerSaaAccreditation"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="installerSaaAccreditation" data-testid="installer-saa" className={inputCls}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {yesNoUnsure.map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="installerApprovedContracts">
                Approved customer contracts &amp; warranty processes?
              </Label>
              <Controller
                control={control}
                name="installerApprovedContracts"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="installerApprovedContracts" data-testid="installer-contracts" className={inputCls}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {yesNoUnsure.map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
            <div>
              <Label htmlFor="installerLandingPrices">
                Standard packages &amp; landing prices?
              </Label>
              <Controller
                control={control}
                name="installerLandingPrices"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger id="installerLandingPrices" data-testid="installer-prices" className={inputCls}>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {yesNoUnsure.map((o) => (
                        <SelectItem key={o} value={o}>{o}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="installerSubcontractPct">
                What percentage of your current work is subcontracted?
              </Label>
              <Input
                id="installerSubcontractPct"
                data-testid="installer-subcontract-pct"
                placeholder="e.g. 80%"
                className={inputCls}
                {...register("installerSubcontractPct")}
              />
            </div>
            <div>
              <Label htmlFor="installerDesiredOutcome">
                What outcome would make this pilot worthwhile?
              </Label>
              <Input
                id="installerDesiredOutcome"
                data-testid="installer-outcome"
                placeholder="e.g. 3 direct installs / month"
                className={inputCls}
                {...register("installerDesiredOutcome")}
              />
            </div>
          </div>
        </div>
      )}

      <div>
        <Label htmlFor="message">
          Briefly describe the business and what you want to achieve
        </Label>
        <Textarea
          id="message"
          data-testid="input-message"
          rows={4}
          className="bg-white border-border-neutral rounded-xl focus-visible:ring-mint text-base"
          {...register("message")}
        />
      </div>

      <div>
        <Label htmlFor="preferredContact">Preferred contact method</Label>
        <Controller
          control={control}
          name="preferredContact"
          render={({ field }) => (
            <Select value={field.value} onValueChange={field.onChange}>
              <SelectTrigger
                id="preferredContact"
                data-testid="select-preferredContact"
                className={inputCls}
              >
                <SelectValue placeholder="Select" />
              </SelectTrigger>
              <SelectContent>
                {contactMethods.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        />
      </div>

      {/* Consent */}
      <div className="flex items-start gap-3 rounded-xl bg-soft p-4">
        <Controller
          control={control}
          name="consent"
          rules={{ required: "Please acknowledge the privacy statement" }}
          render={({ field }) => (
            <Checkbox
              id="consent"
              data-testid="checkbox-consent"
              checked={field.value}
              onCheckedChange={field.onChange}
              className="mt-0.5 border-mid-neutral data-[state=checked]:bg-forest data-[state=checked]:border-forest"
            />
          )}
        />
        <Label htmlFor="consent" className="text-sm text-mid-neutral leading-relaxed cursor-pointer">
          I acknowledge that Green Circle Group will use these details to respond to my
          enquiry, in line with the{" "}
          <a href="/privacy" className="font-semibold text-forest underline">
            Privacy Policy
          </a>
          . *
        </Label>
      </div>
      <FieldError message={errors.consent?.message} />

      {submitError && (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700"
        >
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <p>
            {submitError} Please try again, or email{" "}
            <a href={`mailto:${brand.email}`} className="font-semibold underline">
              {brand.email}
            </a>
            .
          </p>
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting}
        data-testid="submit-enquiry"
        className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-lime px-8 py-4 text-base font-bold text-forest transition-all duration-300 hover:bg-bright-green hover:-translate-y-0.5 shadow-[0_8px_24px_rgba(141,254,63,0.35)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mint focus-visible:ring-offset-2 disabled:opacity-60"
      >
        {isSubmitting ? (
          <span className="inline-flex items-center gap-2">
            <Loader2 className="h-5 w-5 animate-spin" /> Sending…
          </span>
        ) : (
          "Request My Free Growth Assessment"
        )}
      </button>

      <p className="text-xs text-mid-neutral text-center">
        No pressure and no generic agency presentation. The first conversation
        is an opportunity to understand your business and determine whether
        Green Circle Group is the right fit.
      </p>
    </form>
  );
}
